import { supabase } from '@/lib/supabase';
import type { LicensePlan, CompanyLicense, FeatureUsageTracking } from '@lebusiness/types';

export interface SubscriptionWithPlan extends CompanyLicense {
  plan: LicensePlan;
}

export interface UsageStats {
  invoicesCount: number;
  usersCount: number;
  storageUsedMb: number;
  apiCallsCount: number;
}

export interface SubscriptionChangeRequest {
  currentPlanId: string;
  targetPlanId: string;
  billingCycle: 'monthly' | 'yearly';
  proratedAmount: number;
}

/**
 * Service for managing cloud subscriptions
 */
export class SubscriptionService {
  /**
   * Get all available plans
   */
  static async getPlans(): Promise<LicensePlan[]> {
    const { data, error } = await supabase
      .from('license_plans')
      .select('*')
      .eq('is_active', true)
      .order('tier_level', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch plans: ${error.message}`);
    }

    return (data || []).map(this.transformPlanFromDB);
  }

  /**
   * Get current company's active subscription
   */
  static async getCurrentSubscription(companyId: string): Promise<SubscriptionWithPlan | null> {
    const { data, error } = await supabase
      .from('company_licenses')
      .select(`
        *,
        plan:license_plans(*)
      `)
      .eq('company_id', companyId)
      .eq('status', 'active')
      .gt('expires_at', new Date().toISOString())
      .order('expires_at', { ascending: false })
      .limit(1)
      .single();

    if (error || !data) {
      return null;
    }

    return {
      ...this.transformLicenseFromDB(data),
      plan: this.transformPlanFromDB(data.plan),
    };
  }

  /**
   * Get subscription with plan details by ID
   */
  static async getSubscriptionById(licenseId: string): Promise<SubscriptionWithPlan | null> {
    const { data, error } = await supabase
      .from('company_licenses')
      .select(`
        *,
        plan:license_plans(*)
      `)
      .eq('id', licenseId)
      .single();

    if (error || !data) {
      return null;
    }

    return {
      ...this.transformLicenseFromDB(data),
      plan: this.transformPlanFromDB(data.plan),
    };
  }

  /**
   * Get current usage for the company
   */
  static async getCurrentUsage(companyId: string): Promise<UsageStats> {
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM

    const { data, error } = await supabase
      .from('feature_usage_tracking')
      .select('*')
      .eq('company_id', companyId)
      .eq('month', currentMonth)
      .single();

    if (error || !data) {
      // Return empty usage if no data exists
      return {
        invoicesCount: 0,
        usersCount: 0,
        storageUsedMb: 0,
        apiCallsCount: 0,
      };
    }

    return {
      invoicesCount: data.invoices_count || 0,
      usersCount: data.users_count || 0,
      storageUsedMb: data.storage_used_mb || 0,
      apiCallsCount: data.api_calls_count || 0,
    };
  }

  /**
   * Calculate prorated amount for plan change
   */
  static calculateProratedAmount(
    currentPlan: LicensePlan,
    targetPlan: LicensePlan,
    currentExpiryDate: Date,
    billingCycle: 'monthly' | 'yearly'
  ): number {
    const now = new Date();
    const remainingDays = Math.ceil(
      (currentExpiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (remainingDays <= 0) {
      // No proration needed, just charge the full new amount
      return billingCycle === 'yearly' ? targetPlan.priceYearlyDzd : targetPlan.priceMonthlyDzd;
    }

    // Calculate daily rates
    const currentPlanDailyRate = billingCycle === 'yearly'
      ? currentPlan.priceYearlyDzd / 365
      : currentPlan.priceMonthlyDzd / 30;

    const targetPlanDailyRate = billingCycle === 'yearly'
      ? targetPlan.priceYearlyDzd / 365
      : targetPlan.priceMonthlyDzd / 30;

    // Calculate credit for remaining days on current plan
    const currentPlanCredit = currentPlanDailyRate * remainingDays;

    // Calculate cost for remaining days on new plan
    const targetPlanCost = targetPlanDailyRate * remainingDays;

    // Return the difference (can be negative if downgrading)
    return Math.round(targetPlanCost - currentPlanCredit);
  }

  /**
   * Request a plan change
   * This creates a pending change that will be applied after payment
   */
  static async requestPlanChange(
    companyId: string,
    targetPlanId: string,
    billingCycle: 'monthly' | 'yearly'
  ): Promise<{
    requestId: string;
    proratedAmount: number;
    billingCycle: 'monthly' | 'yearly';
  }> {
    // Get current subscription
    const currentSubscription = await this.getCurrentSubscription(companyId);
    if (!currentSubscription) {
      throw new Error('No active subscription found');
    }

    // Get target plan
    const { data: targetPlan, error: planError } = await supabase
      .from('license_plans')
      .select('*')
      .eq('id', targetPlanId)
      .single();

    if (planError || !targetPlan) {
      throw new Error('Target plan not found');
    }

    // Calculate prorated amount
    const proratedAmount = this.calculateProratedAmount(
      currentSubscription.plan,
      this.transformPlanFromDB(targetPlan),
      new Date(currentSubscription.expiresAt),
      billingCycle
    );

    // Create plan change request
    const { data, error } = await supabase
      .from('subscription_change_requests')
      .insert({
        company_id: companyId,
        current_license_id: currentSubscription.id,
        target_plan_id: targetPlanId,
        billing_cycle: billingCycle,
        prorated_amount: proratedAmount,
        status: 'pending_payment',
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create plan change request: ${error.message}`);
    }

    return {
      requestId: data.id,
      proratedAmount,
      billingCycle,
    };
  }

  /**
   * Apply a plan change after successful payment
   */
  static async applyPlanChange(requestId: string): Promise<SubscriptionWithPlan> {
    const { data, error } = await supabase.rpc('apply_subscription_change', {
      p_request_id: requestId,
    });

    if (error) {
      throw new Error(`Failed to apply plan change: ${error.message}`);
    }

    return this.getSubscriptionById(data.license_id);
  }

  /**
   * Cancel a pending plan change request
   */
  static async cancelPlanChange(requestId: string): Promise<void> {
    const { error } = await supabase
      .from('subscription_change_requests')
      .update({ status: 'cancelled', updated_at: new Date().toISOString() })
      .eq('id', requestId);

    if (error) {
      throw new Error(`Failed to cancel plan change: ${error.message}`);
    }
  }

  /**
   * Get pending plan change request
   */
  static async getPendingChangeRequest(companyId: string) {
    const { data, error } = await supabase
      .from('subscription_change_requests')
      .select(`
        *,
        target_plan:license_plans(*)
      `)
      .eq('company_id', companyId)
      .eq('status', 'pending_payment')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error || !data) {
      return null;
    }

    return {
      id: data.id,
      targetPlan: this.transformPlanFromDB(data.target_plan),
      billingCycle: data.billing_cycle,
      proratedAmount: data.prorated_amount,
      createdAt: data.created_at,
    };
  }

  /**
   * Get subscription history (past licenses)
   */
  static async getSubscriptionHistory(companyId: string): Promise<SubscriptionWithPlan[]> {
    const { data, error } = await supabase
      .from('company_licenses')
      .select(`
        *,
        plan:license_plans(*)
      `)
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch subscription history: ${error.message}`);
    }

    return (data || []).map(item => ({
      ...this.transformLicenseFromDB(item),
      plan: this.transformPlanFromDB(item.plan),
    }));
  }

  /**
   * Transform database plan to typed object
   */
  private static transformPlanFromDB(data: any): LicensePlan {
    return {
      id: data.id,
      name: data.name,
      tierLevel: data.tier_level,
      maxUsers: data.max_users,
      maxInvoicesPerMonth: data.max_invoices_per_month,
      enabledFeatures: data.enabled_features || [],
      priceMonthlyDzd: parseFloat(data.price_monthly_dzd),
      priceYearlyDzd: parseFloat(data.price_yearly_dzd),
      isActive: data.is_active,
      description: data.description,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
    };
  }

  /**
   * Transform database license to typed object
   */
  private static transformLicenseFromDB(data: any): CompanyLicense {
    return {
      id: data.id,
      companyId: data.company_id,
      planId: data.plan_id,
      licenseKey: data.license_key,
      licenseType: data.license_type,
      status: data.status,
      issuedAt: new Date(data.issued_at),
      activatedAt: data.activated_at ? new Date(data.activated_at) : null,
      expiresAt: new Date(data.expires_at),
      lastVerifiedAt: data.last_verified_at ? new Date(data.last_verified_at) : null,
      maxDevices: data.max_devices,
      notes: data.notes,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
    };
  }
}

export default SubscriptionService;
